import React, { useCallback, useState } from 'react';
import { LuThumbsDown, LuThumbsUp } from 'react-icons/lu';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/utils/cn';
import { ChatMessageRating, useSubmitFeedback } from '@/views/ai-chat/ai-chat-queries';

import { RenderedMarkdown } from './RenderedMarkdown';
import { UserPromptContent } from './UserPromptContent';

export interface ToolCallPart {
    type: 'tool_call';
    tool: string;
    message: string;
}

export interface TokenPart {
    type: 'token';
    content: string;
}

export type ContentPart = TokenPart | ToolCallPart;

export interface Message {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    parts: ContentPart[];
    timestamp?: string;
    isStreaming?: boolean;
    /** Model is reasoning between visible tokens — shown as a transient status, never part of parts */
    isThinking?: boolean;
    isError?: boolean;
}

interface MessageBubbleProps {
    message: Message;
    conversationId: string | null;
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({ message, conversationId }) => {
    const isUser = message.role === 'user';
    const showThinkingIndicator = message.isStreaming;
    const [rating, setRating] = useState<ChatMessageRating | null>(null);
    const [isDislikeDialogOpen, setIsDislikeDialogOpen] = useState(false);
    const [dislikeFeedback, setDislikeFeedback] = useState('');
    const { mutate: submitFeedback } = useSubmitFeedback();

    const canShowFeedback =
        !isUser && !message.isStreaming && !message.isError && !!message.timestamp && !!conversationId;

    const handleRate = useCallback(
        (newRating: ChatMessageRating) => {
            if (!conversationId || !message.timestamp) return;
            if (newRating === ChatMessageRating.DISLIKE) {
                setIsDislikeDialogOpen(true);
                return;
            }
            setRating(newRating);
            submitFeedback({
                chatConversationGUID: conversationId as any,
                request: { messageTimestamp: message.timestamp, rating: newRating },
            });
        },
        [conversationId, message.timestamp, submitFeedback],
    );

    const handleDislikeSubmit = useCallback(() => {
        if (!conversationId || !message.timestamp) return;
        setRating(ChatMessageRating.DISLIKE);
        submitFeedback({
            chatConversationGUID: conversationId as any,
            request: {
                messageTimestamp: message.timestamp,
                rating: ChatMessageRating.DISLIKE,
                feedback: dislikeFeedback || undefined,
            },
        });
        setIsDislikeDialogOpen(false);
        setDislikeFeedback('');
    }, [conversationId, dislikeFeedback, message.timestamp, submitFeedback]);

    let lastToolStatus: string | null = null;
    if (!isUser) {
        for (let j = message.parts.length - 1; j >= 0; j--) {
            if (message.parts[j].type === 'tool_call') {
                const part = message.parts[j] as ToolCallPart;
                lastToolStatus = part.message || part.tool;
                break;
            }
        }
    }
    const statusText = message.isThinking
        ? lastToolStatus
            ? `Thinking… · last: ${lastToolStatus}`
            : 'Thinking…'
        : lastToolStatus;

    return (
        <>
            <div className={cn('flex min-w-0 flex-col gap-1', isUser ? 'items-end' : 'items-start')}>
                <div
                    className={cn(
                        'min-w-0 text-sm',
                        isUser
                            ? 'text-foreground max-w-[85%] rounded-2xl bg-blue-100 px-4 py-2.5 dark:bg-blue-950'
                            : 'text-foreground w-full overflow-hidden',
                        message.isError && 'text-destructive',
                    )}
                >
                    {isUser ? (
                        message.content && <UserPromptContent content={message.content} />
                    ) : message.isError ? (
                        <p className="leading-relaxed break-words whitespace-pre-wrap">{message.content}</p>
                    ) : (
                        <div className="flex min-w-0 flex-col gap-3">
                            {!message.isStreaming && message.parts.some((p) => p.type === 'tool_call') && (
                                <p className="text-muted-foreground text-xs">Prepared response</p>
                            )}
                            {message.parts.map((part, i) => {
                                if (part.type === 'tool_call') {
                                    // While streaming, the current activity renders in the status
                                    // card below; historical tool parts stay data-only.
                                    return null;
                                }
                                return <RenderedMarkdown key={i} content={part.content} />;
                            })}
                            {message.isStreaming && (message.isThinking || lastToolStatus !== null) && (
                                <div className="border-border bg-muted/30 rounded-xl border p-3">
                                    <p className="text-muted-foreground flex items-center gap-1.5 text-xs">
                                        <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-current" />
                                        {statusText}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                    {showThinkingIndicator && (
                        <span className="inline-flex gap-0.5">
                            <span className="inline-block h-1.5 w-1.5 animate-bounce rounded-full bg-current opacity-60 [animation-delay:-0.3s]" />
                            <span className="inline-block h-1.5 w-1.5 animate-bounce rounded-full bg-current opacity-60 [animation-delay:-0.15s]" />
                            <span className="inline-block h-1.5 w-1.5 animate-bounce rounded-full bg-current opacity-60" />
                        </span>
                    )}
                </div>

                {canShowFeedback && (
                    <div className="flex items-center gap-0.5">
                        <Button
                            variant="ghost"
                            size="icon"
                            className={cn(
                                'h-6 w-6',
                                rating === ChatMessageRating.LIKE
                                    ? 'text-green-500'
                                    : 'text-muted-foreground hover:text-foreground',
                            )}
                            title="Helpful"
                            onClick={() => handleRate(ChatMessageRating.LIKE)}
                        >
                            <LuThumbsUp className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            className={cn(
                                'h-6 w-6',
                                rating === ChatMessageRating.DISLIKE
                                    ? 'text-red-500'
                                    : 'text-muted-foreground hover:text-foreground',
                            )}
                            title="Not helpful"
                            onClick={() => handleRate(ChatMessageRating.DISLIKE)}
                        >
                            <LuThumbsDown className="h-3.5 w-3.5" />
                        </Button>
                    </div>
                )}
            </div>

            <Dialog open={isDislikeDialogOpen} onOpenChange={setIsDislikeDialogOpen}>
                <DialogContent className="max-w-sm">
                    <DialogTitle className="text-sm font-semibold">What could be improved?</DialogTitle>
                    <Textarea
                        value={dislikeFeedback}
                        onChange={(e) => setDislikeFeedback(e.target.value)}
                        placeholder="Optional — tell us what went wrong…"
                        className="min-h-[80px] resize-none text-sm"
                    />
                    <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setIsDislikeDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button size="sm" onClick={handleDislikeSubmit}>
                            Submit
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};
